import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from "utils/api";
import { validatePersonNames } from "utils/personName";
import { setUser } from "state";
import "styles/auth.css";

export const PasswordSettings = ({ user }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const changePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword.length < 8) {
      setPasswordMsg({
        kind: "error",
        text: "Das Passwort muss mindestens 8 Zeichen lang sein.",
      });
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setPasswordMsg({ kind: "error", text: "Die Passwörter stimmen nicht überein." });
      return;
    }
    setPasswordSaving(true);
    try {
      const { data } = await api.post("/auth/profile/password-change", {
        currentPassword,
        newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
      setPasswordMsg({ kind: "success", text: data.message });
    } catch (err) {
      setPasswordMsg({
        kind: "error",
        text: err?.response?.data?.message || "Passwortänderung fehlgeschlagen.",
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <section className="konto-profile" aria-labelledby="konto-password-heading">
      <h2 id="konto-password-heading">Passwort</h2>
      <div className="auth-alert is-info">
        Falls dein Account von uns manuell erstellt worden ist, lautet dein
        Passwort wie folgt: <strong>12345678</strong>
      </div>
      {passwordMsg && (
        <div className={`auth-alert is-${passwordMsg.kind}`}>{passwordMsg.text}</div>
      )}
      <form
        className="auth-form konto-profile-form konto-password-form"
        onSubmit={changePassword}
        autoComplete="off"
      >
        <input
          type="text"
          name="username"
          className="konto-profile-autofill-anchor"
          value={user?.email || ""}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          autoComplete="username"
        />
        <div>
          <label htmlFor="profile-password-current">Aktuelles Passwort</label>
          <input
            id="profile-password-current"
            name="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        <div>
          <label htmlFor="profile-password-new">Neues Passwort</label>
          <input
            id="profile-password-new"
            name="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>
        <div>
          <label htmlFor="profile-password-confirm">Neues Passwort wiederholen</label>
          <input
            id="profile-password-confirm"
            name="new-password-confirm"
            type="password"
            value={newPasswordConfirm}
            onChange={(e) => setNewPasswordConfirm(e.target.value)}
            autoComplete="off"
            minLength={8}
            required
          />
        </div>
        <div className="konto-profile-actions">
          <button type="submit" className="btn-primary" disabled={passwordSaving}>
            {passwordSaving ? "Wird gespeichert…" : "Passwort ändern"}
          </button>
        </div>
      </form>
    </section>
  );
};

const ProfileSettings = ({ brand, afterEmail = null }) => {
  const user = useSelector((s) => s.user);
  const dispatch = useDispatch();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [emailMsg, setEmailMsg] = useState(null);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName || "");
    setLastName(user.lastName || "");
    setPhone(user.phone || "");
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled && data.user) {
          dispatch(setUser({ user: data.user }));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    const nameError = validatePersonNames(firstName, lastName);
    if (nameError) {
      setProfileMsg({ kind: "error", text: nameError });
      return;
    }
    setProfileSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
      });
      dispatch(setUser({ user: data.user }));
      setProfileMsg({ kind: "success", text: data.message });
    } catch (err) {
      setProfileMsg({
        kind: "error",
        text: err?.response?.data?.message || "Speichern fehlgeschlagen.",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const requestEmailChange = async (e) => {
    e.preventDefault();
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    setEmailSaving(true);
    setEmailMsg(null);
    try {
      const body = { newEmail: trimmed };
      if (brand) body.brand = brand;
      const { data } = await api.post("/auth/profile/email-change", body);
      dispatch(setUser({ user: data.user }));
      setNewEmail("");
      setEmailMsg({ kind: "success", text: data.message });
    } catch (err) {
      setEmailMsg({
        kind: "error",
        text: err?.response?.data?.message || "Anfrage fehlgeschlagen.",
      });
    } finally {
      setEmailSaving(false);
    }
  };

  return (
    <section className="konto-profile" aria-labelledby="konto-profile-heading">
      <h2 id="konto-profile-heading">Meine Daten</h2>

      {user?.pendingEmail && (
        <div className="auth-alert is-info konto-profile-pending">
          Bestätigung ausstehend: Wir haben einen Link an{" "}
          <strong>{user.pendingEmail}</strong> gesendet. Deine E-Mail-Adresse
          ändert sich erst nach dem Klick in dieser E-Mail.
        </div>
      )}

      {profileMsg && (
        <div className={`auth-alert is-${profileMsg.kind}`}>{profileMsg.text}</div>
      )}

      <form className="auth-form konto-profile-form" onSubmit={saveProfile}>
        <div className="row">
          <div>
            <label htmlFor="profile-first">Vorname</label>
            <input
              id="profile-first"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
            />
          </div>
          <div>
            <label htmlFor="profile-last">Nachname</label>
            <input
              id="profile-last"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
            />
          </div>
        </div>
        <div>
          <label htmlFor="profile-phone">Telefon</label>
          <input
            id="profile-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            required
          />
        </div>
        <div className="konto-profile-actions">
          <button type="submit" className="btn-primary" disabled={profileSaving}>
            {profileSaving ? "Wird gespeichert…" : "Daten speichern"}
          </button>
        </div>
      </form>

      <h3 className="konto-profile-sub">E-Mail-Adresse</h3>
      {emailMsg && (
        <div className={`auth-alert is-${emailMsg.kind}`}>{emailMsg.text}</div>
      )}
      <form
        className="auth-form konto-profile-form"
        onSubmit={requestEmailChange}
        autoComplete="off"
      >
        <div>
          <label htmlFor="profile-email-current">Aktuelle E-Mail</label>
          <input
            id="profile-email-current"
            name="username"
            type="email"
            value={user?.email || ""}
            readOnly
            aria-readonly="true"
            autoComplete="username"
          />
        </div>
        <div>
          <label htmlFor="profile-email-new">Neue E-Mail</label>
          <input
            id="profile-email-new"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="email"
            placeholder="neue@adresse.de"
            required
          />
        </div>
        <p className="konto-profile-hint">
          Wir senden einen Bestätigungslink an die neue Adresse. Die Änderung
          wird erst nach dem Klick wirksam.
        </p>
        <div className="konto-profile-actions">
          <button type="submit" className="btn-primary" disabled={emailSaving}>
            {emailSaving ? "Wird gesendet…" : "Neue E-Mail bestätigen lassen"}
          </button>
        </div>
      </form>

      {afterEmail}
    </section>
  );
};

export default ProfileSettings;
